import {
  Component,
  HostListener,
  OnDestroy,
  OnInit,
  ViewEncapsulation,
} from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { Title } from "@angular/platform-browser";
import { ActivatedRoute, Router } from "@angular/router";
import { Location } from "@angular/common";
import { select, Store } from "@ngrx/store";
import { TranslateService } from "@ngx-translate/core";
import { Observable, Subject, Subscription } from "rxjs";
import { FuelingModeDTO } from "src/app/models/fueling-mode/fueling-modeDTO";
import { TopCard } from "src/app/models/top-card/top-card.model";
import {
  createFuelingMode,
  getFuelingMode,
  resetFuelingMode,
  updateFuelingModes,
} from "src/app/store/actions/fueling-modes/fueling-modes.actions";
import {
  AppState,
  fuelingModeCreatedState,
  fuelingModeIsLoading,
  loadedFuelingModeState,
} from "src/app/store/reducers";
import { filter, skip, switchMap, take, takeUntil } from "rxjs/operators";
import { SnackbarService } from "src/app/shared/services/snackbar/snackbar.service";
import { FormUtils } from "src/app/shared/utils/formUtils";
import { cloneDeep } from "lodash";
import { SnackbarType } from "src/app/models/snackbar/snackbar.model";
import { CanComponentDeactivate } from "src/app/shared/guard/pending.guard";
import {
  Dialog,
  GuardConfirmationDialogData,
} from "src/app/models/dialog/dialog.model";
import { ConfirmationDialogComponent } from "src/app/shared/modules/dialogs/confirmation-dialog/confirmation-dialog.component";
import { isAuthenticated } from "src/app/shared/utils/authenticationUtils";
import { MatDialog } from "@angular/material/dialog";
import { RolePermissionsService } from "src/app/shared/services/role-permissions/role-permissions.service";
import { FORM_PARAMS } from "../../entity-current-Management/entity-current.component";
export const FUELINGMODE_FORM = {
  description: "description",
  enabled: "enabled",
  maxTimeWithoutProgress: "maxTimeWithoutProgress",
  maxTimeToReachminLimit: "maxTimeToReachminLimit",
  maxTransactionVolume: "maxTransactionVolume",
  maxFuellingTime: "maxFuellingTime",
  maxTransactionAmount: "maxTransactionAmount",
  maxVolumeOverrun: "maxVolumeOverrun",
  clearDisplayTime: "clearDisplayTime",
  clearDisplayWhenTransactionIsCleared: "clearDisplayWhenTransactionIsCleared",
};
@Component({
  selector: "app-fueling-mode-details",
  templateUrl: "./fueling-mode-details.component.html",
  styleUrls: ["./fueling-mode-details.component.scss"],
  encapsulation: ViewEncapsulation.None,
})
export class FuelingModeDetailsComponent
  implements OnInit, OnDestroy, CanComponentDeactivate
{
  //FuelingMode
  isLoading$: Observable<boolean>;
  loadedFuelingMode$: Observable<FuelingModeDTO>;
  created$: Observable<boolean>;

  //top-card configuration
  card: TopCard;
  icon = "add";

  //Subscriptions
  loadedFuelingModeSubs: Subscription;
  createdSub: Subscription;
  protected destroy$ = new Subject<void>();

  // Permissions
  public canWrite$: Observable<boolean>;
  canWrite: boolean = false;

  currentFuelingMode: FuelingModeDTO;
  currCode: string;

  //Actions
  creation: boolean = false;
  duplication: boolean = false;

  //Form
  fuelingModeDataForm: FormGroup;

  //Language
  langSubscription: Subscription;
  constructor(
    private translate: TranslateService,
    private titleService: Title,
    private route: ActivatedRoute,
    private router: Router,
    private store: Store<AppState>,
    private location: Location,
    private snackbarService: SnackbarService,
    private fb: FormBuilder,
    private dialog: MatDialog,
    public rolePermissionsService: RolePermissionsService
  ) {
    this.titleService.setTitle(
      this.translate.instant("FuelingMode.fuelModeDetails")
    );

    this.isLoading$ = this.store.pipe(select(fuelingModeIsLoading));
    this.loadedFuelingMode$ = this.store.pipe(select(loadedFuelingModeState));
    this.created$ = this.store.pipe(select(fuelingModeCreatedState));

    this.card = {
      avatarUrl: "",
      content: "",
      imageUrl: "",
      current: "",
      title: "FuelingMode.newFuelMode",
      subtitle: this.duplication
        ? "FuelingMode.duplication"
        : "FuelingMode.creation",
    };
  }

  confirmDialog(dialog: any, refresh = false): Observable<boolean> {
    const dialogData: Dialog = GuardConfirmationDialogData;
    const dialogRef = dialog.open(ConfirmationDialogComponent, dialogData);
    if (!refresh) {
      return dialogRef.afterClosed();
    }
    dialogRef.afterClosed().subscribe((result: boolean) => {
      if (result) {
        this.reload();
      }
    });
  }

  ngOnInit(): void {
    this.router.routeReuseStrategy.shouldReuseRoute = () => false;
    this.router.onSameUrlNavigation = "ignore";

    this.route.params.subscribe((params) => {
      const code = params.id;
      this.creation = false;
      this.duplication = false;
      const duplicateCode = localStorage.getItem("duplicate");

      this.router.routeReuseStrategy.shouldReuseRoute = () => false;
      if (code && code?.length) {
        this.initialiseState(code, false);
      } else if (duplicateCode !== null) {
        this.duplication = true;
        localStorage.removeItem("duplicate");
        this.initialiseState(duplicateCode, true);
      } else {
        this.creation = true;
        this.initialiseState("", false);
      }
    });
    this.createFuelingModeForm();
    this.loadingFuelingModeInfo();
    this.loadLangChanges();
    this.resetCreation();
  }

  initialiseState(code: string, duplicate?: boolean) {
    if (code) {
      this.loadFuelingMode(code);
      if (duplicate) {
        this.currCode = "";
      } else {
        this.currCode = code;
      }
    } else {
      this.reset();
      this.currCode = "";
      this.card = {
        avatarUrl: "",
        content: "",
        imageUrl: "",
        current: "",
        title: "FuelingMode.newFuelMode",
        subtitle: this.duplication
          ? "FuelingMode.duplication"
          : "FuelingMode.creation",
      };
    }
  }

  loadFuelingMode(code: string) {
    if (code) {
      this.store.dispatch(getFuelingMode({ code: code }));
    }
  }

  reset() {
    this.store.dispatch(resetFuelingMode());
  }

  back() {
    this.router.routeReuseStrategy.shouldReuseRoute = () => false;
    this.location.back();
  }

  refresh() {
    if (isAuthenticated(this.router.url) && !this.fuelingModeDataForm.pristine) {
      this.confirmDialog(this.dialog, true);
      return;
    } else {
      this.reload();
    }
  }
  
  reload() {
    this.router.routeReuseStrategy.shouldReuseRoute = () => false;
    this.router.onSameUrlNavigation = "reload";
    this.router.navigate(["fueling-modes/fueling-mode", this.currCode]);
  }

  new() {
    this.router.routeReuseStrategy.shouldReuseRoute = () => false;
    this.router.onSameUrlNavigation = "reload";
    this.router.navigate(["/fueling-modes/fueling-mode"]);
    this.creation = true;
  }

  duplicate() {
    this.router.routeReuseStrategy.shouldReuseRoute = () => false;
    localStorage.setItem("duplicate", this.currCode);
    this.router.onSameUrlNavigation = "reload";
    this.router.navigate(["fueling-modes/fueling-mode", {}]);
  }

  resetCreation() {
    this.createdSub = this.created$.pipe(skip(1)).subscribe((created) => {
      if (created) {
        this.creation = false;
        this.duplication = false;
      }
    });
  }

  createFuelingModeForm() {
    this.fuelingModeDataForm = this.fb.group({
      [FUELINGMODE_FORM.description]: [
        null,
        [
          Validators.required,
          FormUtils.noWhitespaceValidator,
          Validators.maxLength(50),
        ],
      ],
      [FUELINGMODE_FORM.enabled]: [true],
      [FUELINGMODE_FORM.clearDisplayTime]: [
        null,
        [Validators.min(0), Validators.max(9999), Validators.required],
      ],
      [FUELINGMODE_FORM.clearDisplayWhenTransactionIsCleared]: [null],
      [FUELINGMODE_FORM.maxFuellingTime]: [
        null,
        [Validators.min(0), Validators.max(9999)]
      ],
      [FUELINGMODE_FORM.maxTimeToReachminLimit]: [
        null,
        [Validators.required, Validators.min(0), Validators.max(9999)],
      ],
      [FUELINGMODE_FORM.maxTimeWithoutProgress]: [
        null,
        [Validators.required, Validators.min(0), Validators.max(99)],
      ],
      [FUELINGMODE_FORM.maxTransactionAmount]: [
        null,
        [Validators.required, Validators.max(9999999), Validators.min(0)],
      ],
      [FUELINGMODE_FORM.maxTransactionVolume]: [
        null,
        [Validators.required, Validators.max(9999999), Validators.min(0)],
      ],
      [FUELINGMODE_FORM.maxVolumeOverrun]: [
        null,
        [Validators.required, Validators.max(99), Validators.min(0)],
      ],
    });
  }

  checkPermissions(): void {
      this.rolePermissionsService["roles$"]
        .pipe(
          filter((roles) => roles.length > 0),
          take(1),
          switchMap(() =>
            this.rolePermissionsService.hasPermission(
              "FUELLING_MODE_MANAGEMENT",
              "WRITE"
            )
          ),
          takeUntil(this.destroy$)
        )
        .subscribe((canEdit) => {
          this.canWrite = canEdit;
          if (!canEdit) {
            this.disableFormControls(this.fuelingModeDataForm);
          }
        });
  
      this.canWrite$ = this.rolePermissionsService.hasPermission(
        "FUELLING_MODE_MANAGEMENT",
        "WRITE"
      );
    }
  
    protected disableFormControls(form: FormGroup): void {
      const fields = [
        FUELINGMODE_FORM.description,
        FUELINGMODE_FORM.enabled,
        FUELINGMODE_FORM.maxTimeWithoutProgress,
        FUELINGMODE_FORM.maxTimeToReachminLimit,
        FUELINGMODE_FORM.maxTransactionVolume,
        FUELINGMODE_FORM.maxFuellingTime,
        FUELINGMODE_FORM.maxTransactionAmount,
        FUELINGMODE_FORM.maxVolumeOverrun,
        FUELINGMODE_FORM.clearDisplayTime,
        FUELINGMODE_FORM.clearDisplayWhenTransactionIsCleared,
      ];
  
      fields.forEach((field) => form.get(field)?.disable());
    }

  loadLangChanges() {
    this.langSubscription = this.translate.onLangChange.subscribe(() => {
      this.titleService.setTitle(
        this.translate.instant("FuelingMode.fuelModeDetails")
      );
    });
  }

  loadingFuelingModeInfo() {
    this.loadedFuelingModeSubs = this.loadedFuelingMode$.subscribe(
      (fuelingMode: FuelingModeDTO) => {
        if (fuelingMode) {
          this.currentFuelingMode = cloneDeep(fuelingMode);

          if (
            (!this.currCode ||
              (this.currCode && this.currCode?.length === 0)) &&
            !this.duplication
          ) {
            this.currCode = this.currentFuelingMode?.code;
          }

          this.card = {
            avatarUrl: "",
            content: "",
            imageUrl: "",
            current: !this.duplication ? fuelingMode?.code : "",
            title: !this.duplication
              ? "[" + fuelingMode?.description + "]"
              : "FuelingMode.newFuelMode",
            subtitle: !this.duplication
              ? "FuelingMode.fuelingmode"
              : "FuelingMode.duplication",
          };

          this.fuelingModeDataForm.controls[
            FUELINGMODE_FORM.description
          ].setValue(fuelingMode ? fuelingMode?.description : "");

          this.fuelingModeDataForm.controls[FUELINGMODE_FORM.enabled].setValue(
            fuelingMode ? fuelingMode?.enabled : false
          );

          this.fuelingModeDataForm.controls[
            FUELINGMODE_FORM.clearDisplayTime
          ].setValue(fuelingMode ? fuelingMode?.clearDisplayTime : "");

          this.fuelingModeDataForm.controls[
            FUELINGMODE_FORM.clearDisplayWhenTransactionIsCleared
          ].setValue(
            fuelingMode
              ? fuelingMode?.clearDisplayWhenTransactionIsCleared
              : false
          );

          this.fuelingModeDataForm.controls[
            FUELINGMODE_FORM.maxTimeToReachminLimit
          ].setValue(fuelingMode ? fuelingMode?.maxTimeToReachMinLimit : "");

          this.fuelingModeDataForm.controls[
            FUELINGMODE_FORM.maxTimeWithoutProgress
          ].setValue(fuelingMode ? fuelingMode?.maxTimeWithoutProgress : "");

          this.fuelingModeDataForm.controls[
            FUELINGMODE_FORM.maxFuellingTime
          ].setValue(fuelingMode ? fuelingMode?.maxFuellingTime : "");

          this.fuelingModeDataForm.controls[
            FUELINGMODE_FORM.maxTransactionAmount
          ].setValue(fuelingMode ? fuelingMode?.maxTransactionAmount : "");

          this.fuelingModeDataForm.controls[
            FUELINGMODE_FORM.maxTransactionVolume
          ].setValue(fuelingMode ? fuelingMode?.maxTransactionVolume : "");

          this.fuelingModeDataForm.controls[
            FUELINGMODE_FORM.maxVolumeOverrun
          ].setValue(fuelingMode ? fuelingMode?.maxVolumeOverrun : "");
        }
      }
    );

    this.checkPermissions();
  }

  onSubmit() {
    if (!this.fuelingModeDataForm.valid) {
      this.fuelingModeDataForm.markAllAsTouched();
      this.snackbarService.show({
        message: this.translate.instant("FuelingMode.invalidForm"),
        type: SnackbarType.ERROR,
      });
      return;
    }

    if (
      this.fuelingModeDataForm.pristine &&
      !this.creation &&
      !this.duplication
    ) {
      this.snackbarService.show({
        message: this.translate.instant("FuelingMode.NoChanges"),
        type: SnackbarType.WARNING,
      });
      return;
    }

    let fuelingMode: FuelingModeDTO = {
      description: this.fuelingModeDataForm.controls.description.value,
      clearDisplayWhenTransactionIsCleared:
        this.fuelingModeDataForm.controls.clearDisplayWhenTransactionIsCleared
          .value ?? false,
      maxFuellingTime:
        this.fuelingModeDataForm.controls.maxFuellingTime.value ?? null,
      maxTimeToReachMinLimit:
        this.fuelingModeDataForm.controls.maxTimeToReachminLimit.value ?? null,
      maxTimeWithoutProgress:
        this.fuelingModeDataForm.controls.maxTimeWithoutProgress.value ?? null,
      maxTransactionAmount:
        this.fuelingModeDataForm.controls.maxTransactionAmount.value ?? null,
      clearDisplayTime:
        this.fuelingModeDataForm.controls.clearDisplayTime.value ?? null,
      enabled: this.fuelingModeDataForm.controls.enabled.value ?? false,
      maxTransactionVolume:
        this.fuelingModeDataForm.controls.maxTransactionVolume.value ?? null,
      maxVolumeOverrun:
        this.fuelingModeDataForm.controls.maxVolumeOverrun.value ?? null,
    };

    if (this.currCode.length === 0) {
      this.store.dispatch(createFuelingMode({ fuelingMode: fuelingMode }));
    } else {
      fuelingMode = { ...fuelingMode, code: this.currCode };
      this.store.dispatch(updateFuelingModes({ fuelingMode: fuelingMode }));
    }
    this.fuelingModeDataForm.markAsUntouched();
    this.fuelingModeDataForm.markAsPristine();
  }

  ngOnDestroy(): void {
    if (this.langSubscription) {
      this.langSubscription.unsubscribe();
    }
    if (this.loadedFuelingModeSubs) {
      this.loadedFuelingModeSubs.unsubscribe();
    }
    if (this.createdSub) {
      this.createdSub.unsubscribe();
    }
  }

  @HostListener("window:beforeunload")
  canDeactivate(): Observable<boolean> | boolean {
    return this.isNavigationAllowed();
  }

  isNavigationAllowed(): Observable<boolean> | boolean {
    if (!isAuthenticated(this.router.url) || this.fuelingModeDataForm.pristine) {
      return true;
    } else {
      return false;
    }
  }
}
